import { isHTMLElement } from 'lexical';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Tip from 'src/components/design-system/Tip';

export default function SuggestionTooltipPlugin({
  anchorElem,
}: {
  anchorElem: HTMLElement;
}): React.ReactPortal {
  return createPortal(<SuggestionTooltipInner anchorElem={anchorElem} />, anchorElem);
}

function SuggestionTooltipInner({ anchorElem }: { anchorElem: HTMLElement }) {
  const [tooltip, setTooltip] = useState<string | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(
    null,
  );

  useEffect(() => {
    function handleMouseOver(event: MouseEvent) {
      if (!isHTMLElement(event.target)) return;

      const suggestionEl = (event.target as HTMLElement).closest<HTMLElement>(
        '[data-suggestion][data-tooltip]',
      );

      if (!suggestionEl) {
        setTooltip(null);
        return;
      }

      const text = suggestionEl.dataset.tooltip;
      if (!text) {
        setTooltip(null);
        return;
      }

      const suggestionRect = suggestionEl.getBoundingClientRect();
      const anchorRect = anchorElem.getBoundingClientRect();

      setTooltip(text);
      setPosition({
        top: suggestionRect.top - anchorRect.top,
        left: suggestionRect.left - anchorRect.left,
        width: suggestionRect.width,
      });
    }

    function handleMouseOut(event: MouseEvent) {
      if (!isHTMLElement(event.relatedTarget)) {
        setTooltip(null);
        return;
      }

      const still = (event.relatedTarget as HTMLElement).closest<HTMLElement>(
        '[data-suggestion][data-tooltip]',
      );
      if (!still) {
        setTooltip(null);
      }
    }

    const root = anchorElem;
    root.addEventListener('mouseover', handleMouseOver);
    root.addEventListener('mouseout', handleMouseOut);

    return () => {
      root.removeEventListener('mouseover', handleMouseOver);
      root.removeEventListener('mouseout', handleMouseOut);
    };
  }, [anchorElem]);

  if (!tooltip || !position) return null;

  return (
    <Tip opened position="top-end" label={tooltip}>
      <span
        style={{
          position: 'absolute',
          top: position.top,
          left: position.left,
          width: position.width,
          height: 1,
          pointerEvents: 'none',
        }}
      />
    </Tip>
  );
}
